import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
// CFS (owner, 14 Oct 2026): endless loading kept the footer out of reach on big shelves. The first
// cfsAutoPages() pages after the one shown load by themselves as the shopper nears the end; then a
// "Show more products" button with a count (snippets/product-grid.liquid) loads each further page.
// 70 products before the button was too long a scroll (owner): phones (2 columns) get none, so the
// button follows the first 24; wider screens get one, so it follows 48.
const CFS_PHONE = window.matchMedia('(max-width: 749px)');
const cfsAutoPages = () => (CFS_PHONE.matches ? 0 : 1);

export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** CFS: pages appended by scrolling since this list (or its filters) last changed */
  #cfsAutoLoaded = 0;

  /** @type {MutationObserver | undefined} CFS */
  #cfsRowObserver;

  /** @type {ResizeObserver | undefined} CFS */
  #cfsResizeObserver;

  #cfsTrimQueued = false;

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // CFS: the "Show more products" button (delegated: the grid is re-rendered after filtering)
    this.addEventListener('click', this.#cfsOnMoreClick);

    // CFS: complete rows only while more products are to come (owner, 14 Oct 2026). Re-checked when cards
    // are added or the grid is re-rendered (filters) and when the number of columns can change.
    this.#cfsTrimRow();
    this.#cfsRowObserver = new MutationObserver(() => this.#cfsQueueTrim());
    this.#cfsRowObserver.observe(this, { childList: true, subtree: true });
    this.#cfsResizeObserver = new ResizeObserver(() => this.#cfsQueueTrim());
    this.#cfsResizeObserver.observe(this);

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cfsRowObserver?.disconnect();
    this.#cfsResizeObserver?.disconnect();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                // CFS: by itself only for the first pages; then the button takes over
                if (this.#cfsAutoLoaded >= cfsAutoPages()) {
                  this.#cfsUpdateMore(true);
                } else {
                  this.#cfsAutoLoaded++;
                  const before = this.#cfsCardCount();
                  this.#renderNextPage().then(() => this.#cfsUpdateMore());
                  // Horizon's loader can stall when a page is slow to arrive (seen on the dev server, and in
                  // the original code too): if nothing was added after a few seconds, offer the button,
                  // whose fresh load gets things moving again
                  setTimeout(() => {
                    if (this.isConnected && this.#cfsCardCount() === before) this.#cfsUpdateMore(true);
                  }, 4000);
                }
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    // CFS: a failed request threw here and left the waiting render hanging for good; always resolve
    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch {
      // the render finds no page and returns; the "Show more products" button can try again
    }
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    let pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    // CFS (owner, 14 Oct 2026): an empty or broken reply (seen on the dev server; a dropped connection on
    // a phone) was stored and blocked the rest of the shelf for good. Try once more past the cache, and
    // keep only a page that has products.
    if (!pageContent.includes('ref="cards[]"')) {
      pageContent = await sectionRenderer.getSectionHTML(this.sectionId, false, pageInfo.url);
    }
    if (!pageContent.includes('ref="cards[]"')) return;
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    // CFS: the CFS product card has no [ref="cardGallery"], so there is no helper; this threw and skipped the rest
    this.#aspectRatioHelper?.processNewElements();

    await yieldToMainThread();

    // CFS: replaceState, not pushState: keeps ?page= in step without a Back step per loaded page (it never ran before the fix above)
    history.replaceState(history.state, '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    // CFS: the CFS product card has no [ref="cardGallery"], so there is no helper; this threw and skipped the rest
    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    // CFS: replaceState, as for the next page
    history.replaceState(history.state, '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * CFS: shows (or keeps) the "Show more products" box with "Showing n of total", or hides it once
   * the last page is in. `reveal` shows it the first time (the shopper reached the end of the auto pages).
   * @param {boolean} [reveal]
   */
  #cfsUpdateMore(reveal = false) {
    const { cfsMore, cfsMoreCount, grid } = this.refs;
    if (!(cfsMore instanceof HTMLElement) || !grid) return;
    const more = this.#shouldUsePage(this.#getPage('next'));
    if (!more) {
      cfsMore.hidden = true;
      return;
    }
    if (reveal) cfsMore.hidden = false;
    if (cfsMore.hidden || !(cfsMoreCount instanceof HTMLElement)) return;
    const shown = this.#cfsCardCount();
    const total = Number(cfsMore.dataset.total) || 0;
    cfsMoreCount.textContent = total ? `Showing ${shown} of ${total} products` : '';
  }

  /** CFS: product cards the shopper can see now (held-back cards of an unfinished row excluded) */
  #cfsCardCount() {
    return this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]:not(.cfs-row-spill)').length ?? 0;
  }

  #cfsQueueTrim() {
    if (this.#cfsTrimQueued) return;
    this.#cfsTrimQueued = true;
    requestAnimationFrame(() => {
      this.#cfsTrimQueued = false;
      this.#cfsTrimRow();
    });
  }

  /**
   * CFS: 24 products a page leave an unfinished last row at 5 columns (72 = 14 rows and 2). While more
   * pages are to come, the cards after the last full row wait (class cfs-row-spill, hidden in
   * cfs-collection.css) and show with the next page; the last page shows everything. Never hides the
   * card the shopper is returning to (#hash from the back-button position).
   */
  #cfsTrimRow() {
    const { grid } = this.refs;
    if (!grid) return;
    const cards = /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(':scope > [ref="cards[]"]')]);
    let spill = 0;
    if (this.#shouldUsePage(this.#getPage('next'))) {
      const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
      if (columns > 1) spill = cards.length % columns;
    }
    const held = new Set(spill ? cards.slice(cards.length - spill) : []);
    const target = window.location.hash.length > 1 ? grid.querySelector(`[id="${CSS.escape(window.location.hash.slice(1))}"]`) : null;
    if (target && held.has(/** @type {HTMLElement} */ (target))) held.clear();
    for (const card of cards) {
      const hide = held.has(card);
      if (card.classList.contains('cfs-row-spill') !== hide) card.classList.toggle('cfs-row-spill', hide);
    }
    this.#cfsUpdateMore();
  }

  /** CFS: loads the next page from the button, keeping the button in place for the one after */
  #cfsOnMoreClick = async (/** @type {MouseEvent} */ event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest?.('[ref="cfsMoreButton"]');
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Loading…';
    try {
      await this.#renderNextPage();
    } finally {
      button.removeAttribute('aria-busy');
      button.textContent = 'Show more products';
      this.#cfsUpdateMore();
    }
  };

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();
    this.#cfsAutoLoaded = 0; // CFS: new results load by themselves again at first

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
